import type { FullPokemonCard } from '@/types/pokemon'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

interface CardDetailsProps {
  card: FullPokemonCard
}

function formatDate(date: Date | null): string {
  if (!date) return 'N/A'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export const CardDetails = ({ card }: CardDetailsProps) => {
  return (
    <Card className="w-full">
      <CardHeader className="border-b">
        <CardTitle className="text-xl sm:text-2xl break-words">{card.name}</CardTitle>
        {card.cardMarketUrl && (
          <a href={card.cardMarketUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline mt-2 inline-block">
            View on Cardmarket →
          </a>
        )}
      </CardHeader>

      <CardContent className="px-4 sm:px-6">
        {/* Set Information Section */}
        {card.set && (
          <>
            <div className="mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                <span className="border-l-4 border-blue-500 pl-2 sm:pl-3">Set Information</span>
              </h3>
              <div className="bg-muted/30 rounded-lg p-3 sm:p-4 border">
                <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full">
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Set Name</div>
                      <div className="text-sm sm:text-base font-bold break-words">{card.set.name}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Series</div>
                      <div className="text-sm sm:text-base font-medium">{card.set.series}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Release Date</div>
                      <div className="text-sm sm:text-base font-medium">{formatDate(card.set.releasedAt)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Total Cards</div>
                      <div className="text-sm sm:text-base font-medium">{card.collectorNumber}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Separator className="my-4 sm:my-6" />
          </>
        )}

        {/* Basic Card Information */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            <span className="border-l-4 border-blue-500 pl-2 sm:pl-3">Card Details</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <InfoItem label="Card ID" value={card.set.code + '-' + card.collectorNumber} />
            <InfoItem label="Card Number" value={card.collectorNumber} />
            <InfoItem label="Supertype" value={card.cardData.supertype} />

            {card.cardData.subtypes && card.cardData.subtypes.length > 0 && <InfoItem label="Subtypes" value={card.cardData.subtypes.join(', ')} />}
            {card.cardData.hp && <InfoItem label="HP" value={card.cardData.hp} />}
            {card.cardData.artist && <InfoItem label="Artist" value={card.cardData.artist} />}
          </div>
        </div>

        <Separator className="my-4 sm:my-6" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Left Column */}
          <div className="space-y-4 sm:space-y-6">
            {card.cardData.attacks && card.cardData.attacks.length > 0 && (
              <div className="space-y-2 sm:space-y-3">
                <h4 className="text-sm sm:text-base font-semibold border-l-4 border-blue-500 pl-2 sm:pl-3">Attacks</h4>
                <div className="space-y-2 sm:space-y-3">
                  {card.cardData.attacks?.map((attack, idx) => (
                    <div key={idx} className="bg-muted/50 rounded-lg p-3 sm:p-4 border">
                      <div className="flex flex-col gap-2 mb-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="font-bold text-sm sm:text-base break-words">{attack.name}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const InfoItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
    <span className="text-sm sm:text-base font-medium break-words">{value}</span>
  </div>
)
